import * as v from 'valibot';
import { BaseIssue } from 'valibot';

/**
 * newSchema is a helper to create a valibot schema with a name metadata.
 * This function should always be used to create schemas, so the metadata is always present for logging.
 *
 * @example
 * export const UserSchema = newSchema('UserSchema', v.object({
 *   id: v.number(),
 *   name: v.string(),
 * }));
 * export type User = v.InferType<typeof UserSchema>;
 *
 * @example for lazy
 * export type User = {name: string}
 * export const UserSchema: v.GenericSchema<User> = newSchema(
 *  'UserSchema',
 *  v.object({
 *    name: v.lazy(() => v.string()), // But useless here, just for demonstration
 * });
 *
 * @param schemaName The name of the schema
 * @param schema The valibot schema
 */
export function newSchema<TInput, TOutput>(
  schemaName: string,
  schema: v.BaseSchema<TInput, TOutput, BaseIssue<unknown>>,
) {
  return v.pipe(schema, v.metadata({ schemaName }));
}

/**
 * getSchemaName retrieves the schema name from the metadata of a valibot schema.
 *
 * @param schema The valibot schema
 * @returns The schema name or undefined if not set
 */
export function getSchemaName(schema: v.BaseSchema<unknown, unknown, BaseIssue<unknown>>): string | undefined {
  const metadata = v.getMetadata(schema);
  return metadata.schemaName as string | undefined;
}
